import {Suspense, type CSSProperties, type ReactNode, type Ref} from 'react';
import {Navigate, Outlet} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {MapPin} from 'lucide-react';
import {CtaDock, Skeleton, cn} from '@chekinapp/ui';
import {AUTH_INIT_MODELS} from '@chekin/core';
import {LocaleButton} from 'components/LanguageButton';
import {PropertyLinkPhoto, useArrivalProperty} from 'components/arrival';
import {ArrivalPropertyProvider} from '@chekin/shared/guest-form';
import {usePath} from 'hooks';
import {isTerminalSession} from 'hooks/terminalMode';
import {useChekinSDK} from 'context/ChekinSDK';
import {PATHS} from 'routes/constants';
import {MethodSwitchProvider} from '../useMethodSwitch';
import {PROPERTY_LINK_ROOT_CLASSES} from './propertyLinkConstants';

type Props = {
  children?: ReactNode;
  skeleton?: boolean;
  dock?: ReactNode;
  aside?: ReactNode;
  className?: string;
  columnRef?: Ref<HTMLDivElement>;
};

function PropertyLinkHeader() {
  const {t} = useTranslation();
  const property = useArrivalProperty();
  const {accountName} = useChekinSDK();

  const isPlaceholder = property.isLoading;
  const title = property.name || accountName;
  const location = [property.city, property.country].filter(Boolean).join(', ');

  const welcome = (
    <div className="flex flex-col">
      {isPlaceholder ? (
        <span className="flex h-[19px] items-center desk:h-[22px]">
          <Skeleton onScene className="h-[11px] w-20 desk:h-[13px] desk:w-24" />
        </span>
      ) : (
        title && (
          <p className="text-[13px] font-semibold text-[color:var(--chekin-on-scene-text-eyebrow)] desk:text-[15px]">
            {t('arrival_welcome_to')}
          </p>
        )
      )}
      {isPlaceholder ? (
        <span data-app-loader className="mt-0.5 flex h-[33px] items-center desk:h-[62px]">
          <Skeleton
            onScene
            className="h-[26px] w-[190px] rounded-[10px] desk:h-[46px] desk:w-[360px] desk:rounded-[14px]"
          />
        </span>
      ) : (
        <p className="mt-0.5 text-[30px] font-bold leading-[1.1] tracking-[-0.015em] text-white desk:text-[56px]">
          {title || t('arrival_online_check_in')}
        </p>
      )}
      <div className="mt-2 flex items-center justify-between gap-2.5 desk:mt-4 desk:justify-start desk:gap-4">
        {isPlaceholder ? (
          <Skeleton onScene className="h-[13px] w-28 desk:h-[14px] desk:w-32" />
        ) : location ? (
          <p className="inline-flex min-w-0 items-center gap-[5px] text-[13px] font-medium text-[color:var(--chekin-on-scene-text-meta)] desk:text-[14px]">
            <MapPin size={15} className="shrink-0" />
            <span className="truncate">{location}</span>
          </p>
        ) : (
          <span className="desk:hidden" />
        )}
        <LocaleButton
          variant="glass"
          withChevron
          className="h-[30px] shrink-0 pl-2.5 pr-2 text-[12px] [--language-button-glass-bg:rgba(255,255,255,0.18)] [backdrop-filter:blur(14px)_saturate(1.6)] desk:h-[34px] desk:px-3 desk:text-[13px]"
        />
      </div>
    </div>
  );

  return (
    <div
      data-entrance="property-header"
      className="relative h-[210px] shrink-0 overflow-hidden tab:overflow-visible desk:fixed desk:inset-0 desk:h-auto"
    >
      <PropertyLinkPhoto className="tab:fixed" />
      <div className="absolute inset-x-5 bottom-10 z-[1] desk:bottom-16 desk:left-16 desk:right-auto desk:max-w-[min(600px,46%)]">
        {welcome}
      </div>
    </div>
  );
}

function PropertyLinkFrame({children}: {children: ReactNode}) {
  const property = useArrivalProperty();

  return (
    <ArrivalPropertyProvider value={property}>
      <div className={PROPERTY_LINK_ROOT_CLASSES}>
        <PropertyLinkHeader />
        {children}
      </div>
    </ArrivalPropertyProvider>
  );
}

function PropertyLinkLayout() {
  const {modelName} = useChekinSDK();
  const {getFullPath} = usePath();

  if (modelName === AUTH_INIT_MODELS.reservation && !isTerminalSession()) {
    return <Navigate to={getFullPath(PATHS.home)} replace />;
  }

  return (
    <PropertyLinkFrame>
      <MethodSwitchProvider>
        <Suspense fallback={<PropertyLinkShell skeleton />}>
          <Outlet />
        </Suspense>
      </MethodSwitchProvider>
    </PropertyLinkFrame>
  );
}

function PropertyLinkShell({
  children,
  skeleton,
  dock,
  aside,
  className,
  columnRef,
}: Props) {
  const property = useArrivalProperty();

  const isPlaceholder = skeleton || property.isLoading;
  const formPlaceholder = (
    <div aria-busy data-app-loader>
      <Skeleton className="h-6 w-[62%] rounded-[9px] desk:h-8" />
      <Skeleton className="mt-2 h-3.5 w-full" />
      <Skeleton className="mt-1.5 h-3.5 w-[55%]" />
      <Skeleton className="mt-3.5 h-12 w-full rounded-[14px]" />
      <Skeleton className="mt-5 h-[52px] w-full rounded-[14px]" />
      <Skeleton className="mt-2 h-3 w-[70%]" />
    </div>
  );

  const dockPlaceholder = <Skeleton className="h-12 w-full rounded-xl" />;
  const dockContent = isPlaceholder ? dockPlaceholder : dock;

  return (
    <main
      data-entrance="property-card"
      className={cn(
        'relative z-[1] -mt-[26px] flex flex-1 flex-col tab:px-5 tab:pb-12',
        'desk:pointer-events-none desk:m-0 desk:flex-row desk:items-center desk:justify-end desk:py-[60px] desk:pl-[60px] desk:pr-[6vw]',
        className,
      )}
    >
      <div
        ref={columnRef}
        className="mx-auto flex w-full max-w-[560px] flex-1 flex-col rounded-t-[26px] bg-white tab:flex-none tab:overflow-clip tab:rounded-[26px] tab:shadow-[0_30px_70px_rgba(10,10,40,0.28)] desk:pointer-events-auto desk:mx-0 desk:w-[500px] desk:flex-none desk:rounded-none desk:bg-transparent"
      >
        <section className="flex-1 px-5 pb-6 pt-[22px] desk:rounded-b-[24px] desk:rounded-t-[30px] desk:bg-white desk:px-[34px] desk:pb-[26px] desk:pt-[34px]">
          {isPlaceholder ? formPlaceholder : children}
          {dockContent && (
            <div className="mt-6 hidden flex-col gap-3 desk:-mx-1.5 desk:flex">
              {dockContent}
            </div>
          )}
        </section>

        {!isPlaceholder && aside && <div className="mt-3 hidden desk:block">{aside}</div>}

        {dockContent && (
          <CtaDock
            className="tab:[--cta-dock-elevated-shadow:inset_0_1px_0_var(--chekin-divider-subtle)] desk:hidden"
            style={
              {
                '--cta-dock-bg': 'var(--chekin-color-white)',
                '--cta-dock-gap': '10px',
                '--cta-dock-elevated-shadow': 'var(--chekin-shadow-lift-up)',
              } as CSSProperties
            }
            elevated
          >
            {dockContent}
          </CtaDock>
        )}
      </div>
    </main>
  );
}

export {PropertyLinkFrame, PropertyLinkHeader, PropertyLinkLayout, PropertyLinkShell};
